import { useCallback } from 'react';
import { Link } from 'react-router-dom';

import Button from '@mui/material/Button';
import MuiLink from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { EMPTY_STRING } from '@e-socna/empty';
import { buildPath } from '@e-socna/utils';
import type { Id } from '@e-socna/data-layer';

import { PATH_PERSON_GENERAL } from '../../app/constants/paths.constant';
import { EllipsisTooltip } from '../../components/EllipsisTooltip';

import { CreationStatusIndicator } from './CreationStatusIndicator';
import { useDirectorPersonCreation } from './hooks/useDirectorPersonCreation.hook';
import { formatDirectorName } from './utils/format-director-name.util';
import type { DirectorSettledOutcome } from './hooks/useDirectorPersonCreation.hook';
import type { OrganizationSearchDirectorPersonnePhysique } from './types/organization-search-result.type';

interface Props {
  readonly canStart: boolean;
  readonly director: OrganizationSearchDirectorPersonnePhysique;
  readonly fallbackCivilityId: Id;
  readonly index: number;
  readonly isSelected: boolean;
  readonly onSettled: (
    index: number,
    outcome: Readonly<DirectorSettledOutcome>,
  ) => void;
}

export const DirectorResultRow = ({
  canStart,
  director,
  fallbackCivilityId,
  index,
  isSelected,
  onSettled,
}: Readonly<Props>) => {
  const handleSettled = useCallback(
    (outcome: Readonly<DirectorSettledOutcome>) => {
      onSettled(index, outcome);
    },
    [index, onSettled],
  );

  const { errorMessage, personId, retry, status } = useDirectorPersonCreation({
    canStart,
    director,
    fallbackCivilityId,
    onSettled: handleSettled,
  });

  const directorLabel = `${formatDirectorName(director)}${director.qualite ? ` - ${director.qualite}` : EMPTY_STRING}`;

  return (
    <Stack
      direction="row"
      sx={{
        alignItems: 'center',
        gap: 1.5,
        justifyContent: 'space-between',
        opacity: isSelected ? 1 : 0.5,
      }}
    >
      <EllipsisTooltip title={directorLabel}>
        <Typography
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          variant="body2"
        >
          {directorLabel}
        </Typography>
      </EllipsisTooltip>
      {isSelected ? (
        <Stack
          direction="row"
          sx={{ alignItems: 'center', flexShrink: 0, gap: 1 }}
        >
          <CreationStatusIndicator
            infoMessage={errorMessage}
            onRetry={status === 'error' ? retry : undefined}
            status={status}
          />
          {status === 'success' && personId ? (
            <>
              <Button
                component={Link}
                size="small"
                to={buildPath(PATH_PERSON_GENERAL, { id: personId })}
              >
                Consulter
              </Button>
              <Button
                component={MuiLink}
                endIcon={<OpenInNewIcon />}
                href={buildPath(PATH_PERSON_GENERAL, { id: personId })}
                rel="noopener noreferrer"
                size="small"
                target="_blank"
              >
                Nouvel onglet
              </Button>
            </>
          ) : null}
        </Stack>
      ) : null}
    </Stack>
  );
};
